import { useEffect, useState } from "react";
import api from "../../services/api";
import { useTheme } from "../ThemeProvider";
import usePermiso from "../../hooks/usePermiso";
import { usePropiedadUi } from "../../hooks/usePropiedadUi";
import { fichaResumenFontSize } from "./fichaResumenTypography";
import { dialogs } from "../../context/DialogContext";

/** +1px en modo compacto (ficha resumen lateral). */
function frFont(px, compacto) {
  return compacto ? fichaResumenFontSize(px) : px;
}

function fmtFechaHora(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("es-ES", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function DetalleActividadCelda({ fila, theme, compacto, tv }) {
  const t = theme.colors;
  const corto = fila.detalle || fila.titulo || "—";
  const completo = fila.detalle_completo || fila.descripcion || corto;
  const mostrarLupa =
    fila.necesita_lupa === true ||
    (typeof completo === "string" &&
      (completo.includes("\n") || completo.length > 90 || completo !== corto));

  const abrirDetalle = (e) => {
    e?.stopPropagation?.();
    const cambios = Array.isArray(fila.cambios) ? fila.cambios : [];
    let msg = "";
    if (fila.tipo_label) msg += `${fila.tipo_label}\n\n`;
    if (cambios.length) {
      msg += cambios
        .map((c) => {
          const et = c.etiqueta || c.campo || "Campo";
          return `• ${et}\n   ${c.valor_anterior ?? "—"} → ${c.valor_nuevo ?? "—"}`;
        })
        .join("\n\n");
    } else {
      msg += String(completo || "—");
    }
    if (fila.actor_nombre || fila.agente_nombre) {
      msg += `\n\nUsuario: ${fila.actor_nombre || fila.agente_nombre}`;
    }
    if (fila.fecha) {
      msg += `\nFecha: ${fmtFechaHora(fila.fecha)}`;
    }
    dialogs.alert(msg);
  };

  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 6, maxWidth: "100%" }}>
      <span
        style={{
          flex: 1,
          minWidth: 0,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
        title={mostrarLupa ? undefined : String(completo)}
      >
        {corto}
      </span>
      {mostrarLupa && (
        <button
          type="button"
          onClick={abrirDetalle}
          title={tv("Ver detalle completo")}
          aria-label={tv("Ver detalle completo")}
          style={{
            flexShrink: 0,
            border: `1px solid ${t.border}`,
            background: theme.name === "dark" ? "#1f2937" : "#f8fafc",
            color: t.textPrimary,
            borderRadius: 6,
            width: 26,
            height: 26,
            padding: 0,
            cursor: "pointer",
            fontSize: frFont(13, compacto),
            lineHeight: 1,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          🔍
        </button>
      )}
    </div>
  );
}

function fmtDuracion(seg) {
  const n = Number(seg);
  if (!Number.isFinite(n) || n <= 0) return "—";
  if (n < 60) return `${n} s`;
  const m = Math.floor(n / 60);
  const s = n % 60;
  return s ? `${m} min ${s} s` : `${m} min`;
}

function celda(v) {
  if (v == null || v === "") return "—";
  return v;
}

const TARJETAS_RESUMEN = [
  {
    key: "leads",
    emoji: "📥",
    label: "Leads",
    subtitulo: "Interesados en esta ref.",
    color: "#2563eb",
    bgLight: "linear-gradient(145deg, #eff6ff 0%, #dbeafe 55%, #bfdbfe 100%)",
    bgDark: "linear-gradient(145deg, #1e3a8a44 0%, #2563eb28 50%, #1d4ed822 100%)",
  },
  {
    key: "citas",
    emoji: "📅",
    label: "Citas",
    subtitulo: "En agenda vinculadas",
    color: "#7c3aed",
    bgLight: "linear-gradient(145deg, #f5f3ff 0%, #ede9fe 55%, #ddd6fe 100%)",
    bgDark: "linear-gradient(145deg, #4c1d9544 0%, #7c3aed28 50%, #6d28d922 100%)",
  },
  {
    key: "ofertas",
    emoji: "💰",
    label: "Ofertas",
    subtitulo: "En el historial",
    color: "#d97706",
    bgLight: "linear-gradient(145deg, #fffbeb 0%, #fef3c7 55%, #fde68a 100%)",
    bgDark: "linear-gradient(145deg, #78350f44 0%, #d9770628 50%, #b4530922 100%)",
  },
];

function ResumenHistorial({ leads, citas, ofertas, theme, compacto, tv = (x) => x }) {
  const t = theme.colors;
  const isDark = theme.name === "dark";
  const valores = {
    leads: leads ?? 0,
    citas: citas ?? 0,
    ofertas: ofertas ?? 0,
  };

  return (
    <div style={{ marginBottom: compacto ? 16 : 26 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: compacto ? 8 : 12,
        }}
      >
        <span style={{ fontSize: compacto ? frFont(15, compacto) : 18 }} aria-hidden>
          ✨
        </span>
        <span style={{ fontSize: compacto ? frFont(11, compacto) : 12, fontWeight: 700, color: t.textPrimary, letterSpacing: "0.02em" }}>
          {tv("Resumen de actividad")}
        </span>
        <span
          style={{
            flex: 1,
            height: 1,
            background: `linear-gradient(90deg, ${isDark ? "#6366f1" : "#a5b4fc"}66, transparent)`,
          }}
        />
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: compacto ? "repeat(3, minmax(0, 1fr))" : "repeat(3, minmax(0, 1fr))",
          gap: compacto ? 8 : 12,
        }}
      >
        {TARJETAS_RESUMEN.map((tarjeta) => {
          const valor = valores[tarjeta.key];
          const activa = valor > 0;
          return (
            <div
              key={tarjeta.key}
              style={{
                position: "relative",
                borderRadius: compacto ? 10 : 14,
                padding: compacto ? "10px 10px" : "14px 16px",
                background: isDark ? tarjeta.bgDark : tarjeta.bgLight,
                border: `1px solid ${tarjeta.color}${activa ? (isDark ? "66" : "55") : "30"}`,
                boxShadow: activa
                  ? isDark
                    ? `0 6px 24px ${tarjeta.color}28, inset 0 1px 0 ${tarjeta.color}22`
                    : `0 6px 20px ${tarjeta.color}22, inset 0 1px 0 rgba(255,255,255,0.7)`
                  : isDark
                    ? "0 2px 8px rgba(0,0,0,0.2)"
                    : "0 2px 8px rgba(0,0,0,0.04)",
                display: "flex",
                alignItems: "center",
                gap: compacto ? 8 : 14,
                overflow: "hidden",
                transition: compacto ? undefined : "transform 0.15s ease, box-shadow 0.15s ease",
              }}
              onMouseEnter={
                compacto
                  ? undefined
                  : (e) => {
                      e.currentTarget.style.transform = "translateY(-2px)";
                    }
              }
              onMouseLeave={
                compacto
                  ? undefined
                  : (e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                    }
              }
            >
              <div
                style={{
                  width: compacto ? 38 : 50,
                  height: compacto ? 38 : 50,
                  borderRadius: compacto ? 10 : 14,
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: compacto ? frFont(20, compacto) : 26,
                  lineHeight: 1,
                  background: isDark ? `${tarjeta.color}33` : `${tarjeta.color}1a`,
                  border: `1px solid ${tarjeta.color}44`,
                  boxShadow: `0 2px 8px ${tarjeta.color}25`,
                }}
                aria-hidden
              >
                {tarjeta.emoji}
              </div>
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontSize: compacto ? frFont(11, compacto) : 11,
                    fontWeight: 700,
                    color: tarjeta.color,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    marginBottom: 2,
                  }}
                >
                  {tv(tarjeta.label)}
                </div>
                <div
                  style={{
                    fontSize: compacto ? frFont(22, compacto) : 30,
                    fontWeight: 800,
                    color: t.textPrimary,
                    lineHeight: 1,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {valor}
                </div>
                {!compacto && (
                  <div style={{ fontSize: 10, color: t.textMuted, marginTop: 4 }}>{tv(tarjeta.subtitulo)}</div>
                )}
              </div>
              {activa && (
                <span
                  style={{
                    position: "absolute",
                    top: 10,
                    right: 10,
                    fontSize: compacto ? frFont(14, compacto) : 14,
                    opacity: 0.85,
                  }}
                  title="Con actividad"
                  aria-hidden
                >
                  🔥
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Bloque({ titulo, icono, children, theme, vacio, hayDatos, compacto, tv = (x) => x }) {
  const t = theme.colors;
  return (
    <div style={{ marginBottom: compacto ? 18 : 28 }}>
      <h3
        style={{
          fontSize: frFont(13, compacto),
          fontWeight: 700,
          color: t.textPrimary,
          margin: "0 0 10px 0",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <span>{icono}</span>
        {tv(titulo)}
      </h3>
      {!hayDatos ? (
        <p
          style={{
            fontSize: frFont(12, compacto),
            color: t.textMuted,
            padding: "12px 14px",
            borderRadius: 8,
            background: t.bgInput,
            border: `1px solid ${t.border}`,
            margin: 0,
          }}
        >
          {tv(vacio)}
        </p>
      ) : (
        children
      )}
    </div>
  );
}

function TablaHistorial({ columnas, filas, theme, filaDestacada, compacto = false, tv = (x) => x }) {
  const t = theme.colors;
  const thSt = {
    padding: "8px 10px",
    textAlign: "left",
    fontSize: frFont(10, compacto),
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    color: t.textMuted,
    borderBottom: `2px solid ${t.border}`,
    whiteSpace: "nowrap",
  };
  const tdSt = {
    padding: "9px 10px",
    fontSize: frFont(12, compacto),
    color: t.textPrimary,
    borderBottom: `1px solid ${t.border}`,
    verticalAlign: "top",
  };

  return (
    <div style={{ overflowX: "auto", borderRadius: 8, border: `1px solid ${t.border}` }}>
      <table style={{ width: "100%", borderCollapse: "collapse", background: t.bgInput }}>
        <thead>
          <tr style={{ background: theme.name === "dark" ? "#1f2937" : "#f8fafc" }}>
            {columnas.map((col) => (
              <th key={col.id} style={{ ...thSt, minWidth: col.minWidth }}>
                {col.label ? tv(col.label) : ""}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filas.map((fila) => {
            const destacada = filaDestacada?.(fila);
            return (
              <tr
                key={fila._key}
                style={{
                  background: destacada
                    ? theme.name === "dark"
                      ? "#42200644"
                      : "#fffbeb"
                    : "transparent",
                }}
              >
                {columnas.map((col) => (
                  <td key={col.id} style={tdSt}>
                    {col.render ? col.render(fila) : celda(fila[col.id])}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function SeccionHistorial(props) {
  const { propiedadId, referencia, compacto = false } = props;
  const { tv } = usePropiedadUi(props);
  const { theme } = useTheme();
  const t = theme.colors;
  const { puede } = usePermiso();
  const puedeVerHistorial = puede("historial", "ver");
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!propiedadId || !puedeVerHistorial) {
      setData(null);
      return;
    }
    let cancelled = false;
    setCargando(true);
    setError("");
    api
      .get(`/propiedades/${propiedadId}/historial-ficha`)
      .then((res) => {
        if (!cancelled) setData(res.data?.data || null);
      })
      .catch((e) => {
        if (!cancelled) setError(e?.response?.data?.error || "No se pudo cargar el historial");
      })
      .finally(() => {
        if (!cancelled) setCargando(false);
      });
    return () => {
      cancelled = true;
    };
  }, [propiedadId, puedeVerHistorial]);

  if (!puedeVerHistorial) return null;

  if (!propiedadId) {
    return (
      <p style={{ fontSize: frFont(13, compacto), color: t.textMuted, margin: 0 }}>
        {tv("Guarda la propiedad para ver el historial de actividad (leads, citas, ofertas y portales).")}
      </p>
    );
  }

  if (cargando) {
    return <p style={{ fontSize: frFont(13, compacto), color: t.textMuted }}>{tv("Cargando historial…")}</p>;
  }

  if (error) {
    return <p style={{ fontSize: frFont(13, compacto), color: "#ef4444" }}>{error}</p>;
  }

  if (!data) return null;

  const leads = data.leads || [];
  const citas = data.citas || [];
  const ofertas = data.ofertas || [];
  const portales = data.portales || [];
  const actividad = data.actividad || [];
  const resumen = data.resumen || {};

  const colsLeads = [
    { id: "fecha", label: "Fecha", minWidth: 130, render: (r) => fmtFechaHora(r.created_at) },
    { id: "fuente", label: "Procedencia", minWidth: 100 },
    {
      id: "cliente",
      label: "Cliente",
      minWidth: 120,
      render: (r) => [r.nombre, r.apellido].filter(Boolean).join(" ") || "—",
    },
    { id: "telefono", label: "Teléfono", minWidth: 100 },
    { id: "consulta", label: "Consulta", minWidth: 160, render: (r) => r.consulta || "—" },
    { id: "agente_nombre", label: "Agente", minWidth: 120 },
    { id: "etapa", label: "Etapa", minWidth: 80 },
  ];

  const colsCitas = [
    { id: "fecha", label: "Fecha", minWidth: 130, render: (r) => fmtFechaHora(r.fecha_inicio) },
    { id: "cliente", label: "Cliente", minWidth: 120, render: (r) => r.cliente_display || "—" },
    { id: "telefono", label: "Teléfono", minWidth: 100 },
    { id: "agente_nombre", label: "Agente", minWidth: 120 },
    { id: "resultado", label: "Resultado", minWidth: 100 },
    {
      id: "nota",
      label: "",
      minWidth: 110,
      render: (r) =>
        r.segunda_cita ? (
          <span
            style={{
              fontSize: frFont(10, compacto),
              fontWeight: 700,
              padding: "2px 8px",
              borderRadius: 4,
              background: "#f59e0b",
              color: "#fff",
              whiteSpace: "nowrap",
            }}
          >
            2ª {tv("cita")}
          </span>
        ) : (
          "—"
        ),
    },
  ];

  const colsOfertas = [
    { id: "fecha", label: "Fecha", minWidth: 130, render: (r) => fmtFechaHora(r.created_at) },
    { id: "contacto_nombre", label: "Cliente", minWidth: 120 },
    { id: "contacto_telefono", label: "Teléfono", minWidth: 100 },
    { id: "agente_nombre", label: "Agente", minWidth: 120 },
    { id: "resultado", label: "Resultado", minWidth: 100 },
    { id: "contenido", label: "Detalle", minWidth: 160, render: (r) => r.contenido || "—" },
  ];

  return (
    <div>
      <ResumenHistorial
        theme={theme}
        compacto={compacto}
        tv={tv}
        leads={resumen.leads_total ?? leads.length}
        citas={resumen.citas_total ?? citas.length}
        ofertas={resumen.ofertas_total ?? ofertas.length}
      />

      <Bloque
        theme={theme}
        compacto={compacto}
        tv={tv}
        titulo="Leads interesados"
        icono="📥"
        hayDatos={leads.length > 0}
        vacio={`Sin leads con referencia ${referencia || "de esta propiedad"}`}
      >
        <TablaHistorial
          theme={theme}
          compacto={compacto}
          tv={tv}
          columnas={colsLeads}
          filas={leads.map((l) => ({ ...l, _key: l.id }))}
        />
      </Bloque>

      <Bloque
        theme={theme}
        compacto={compacto}
        tv={tv}
        titulo="Citas"
        icono="📅"
        hayDatos={citas.length > 0}
        vacio="Sin citas en agenda vinculadas a esta propiedad"
      >
        <TablaHistorial
          theme={theme}
          compacto={compacto}
          tv={tv}
          columnas={colsCitas}
          filas={citas.map((c) => ({ ...c, _key: c.id }))}
          filaDestacada={(r) => r.segunda_cita}
        />
      </Bloque>

      <Bloque
        theme={theme}
        compacto={compacto}
        tv={tv}
        titulo="Ofertas"
        icono="💰"
        hayDatos={ofertas.length > 0}
        vacio="Sin ofertas registradas en el historial"
      >
        <TablaHistorial
          theme={theme}
          compacto={compacto}
          tv={tv}
          columnas={colsOfertas}
          filas={ofertas.map((o) => ({ ...o, _key: o.id }))}
        />
      </Bloque>

      <Bloque
        theme={theme}
        compacto={compacto}
        tv={tv}
        titulo="Actividad CRM"
        icono="📝"
        hayDatos={actividad.length > 0}
        vacio="Sin cambios de ficha registrados (p. ej. inventario editado)."
      >
        <TablaHistorial
          theme={theme}
          compacto={compacto}
          tv={tv}
          columnas={[
            { id: "fecha", label: "Fecha", minWidth: 130, render: (r) => fmtFechaHora(r.fecha) },
            {
              id: "tipo_label",
              label: "Tipo",
              minWidth: 160,
              render: (r) => (
                <span style={{ fontWeight: 600 }}>{r.tipo_label || r.titulo || r.tipo || "—"}</span>
              ),
            },
            {
              id: "detalle",
              label: "Detalle",
              minWidth: 240,
              render: (r) => (
                <DetalleActividadCelda fila={r} theme={theme} compacto={compacto} tv={tv} />
              ),
            },
            {
              id: "actor",
              label: "Usuario",
              minWidth: 120,
              render: (r) => r.actor_nombre || r.agente_nombre || "—",
            },
          ]}
          filas={actividad.map((a) => ({ ...a, _key: a.id }))}
        />
      </Bloque>

      <Bloque
        theme={theme}
        compacto={compacto}
        tv={tv}
        titulo="Portales"
        icono="🌐"
        hayDatos={portales.length > 0}
        vacio="Sin eventos de portales todavía. Los datos detallados se incorporarán con ScrapingAPI."
      >
        {(resumen.visitas_portal_total > 0 ||
          resumen.favoritos_portal_total > 0 ||
          resumen.duracion_portal_seg > 0) && (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 16,
              fontSize: frFont(11, compacto),
              color: t.textMuted,
              marginBottom: portales.length > 0 ? 12 : 0,
            }}
          >
            <span>{tv("Visitas portal")}: {resumen.visitas_portal_total ?? 0}</span>
            <span>{tv("Favoritos")}: {resumen.favoritos_portal_total ?? 0}</span>
            <span>{tv("Tiempo medio")}: {fmtDuracion(resumen.duracion_portal_seg)}</span>
            {resumen.portal_ultimo && <span>{tv("Último")}: {resumen.portal_ultimo}</span>}
          </div>
        )}
        {portales.length > 0 && (
          <TablaHistorial
            theme={theme}
            compacto={compacto}
            tv={tv}
            columnas={[
              { id: "fecha", label: "Fecha", minWidth: 130, render: (r) => fmtFechaHora(r.fecha) },
              { id: "tipo_label", label: "Tipo", minWidth: 120 },
              { id: "portal", label: "Portal", minWidth: 100 },
              {
                id: "detalle",
                label: "Detalle",
                minWidth: 160,
                render: (r) => {
                  if (r.tipo === "duracion_portal" && r.valor_numero != null) {
                    return fmtDuracion(r.valor_numero);
                  }
                  return r.descripcion || r.valor_texto || "—";
                },
              },
            ]}
            filas={portales.map((p) => ({ ...p, _key: p.id }))}
          />
        )}
      </Bloque>
    </div>
  );
}
